import { useEffect, useRef, useState, memo } from "react";
import type { ToastWithTimer } from "../../../types/toast";
import CloseIcon from "../../../assets/icons/close.svg?react";
import styles from "./toast.module.css";
import {
  TOAST_APPEAR_DURATION,
  TOAST_DISAPPEAR_DURATION,
} from "../../../config/toastConfig";

export interface ToastProps {
  toast: ToastWithTimer;
  onRemove: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onAppearComplete: (id: string) => void;
}

export function Toast({
  toast,
  onRemove,
  onPause,
  onResume,
  onAppearComplete,
}: ToastProps) {
  const [animationState, setAnimationState] = useState<
    "appearing" | "visible" | "disappearing"
  >("appearing");
  const timerRef = useRef<number | null>(null);
  const pausedAtRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(toast.duration);

  useEffect(() => {
    setAnimationState("appearing");
    const appearTimer = window.setTimeout(() => {
      setAnimationState("visible");
      onAppearComplete(toast.id);
    }, TOAST_APPEAR_DURATION);

    return () => {
      window.clearTimeout(appearTimer);
    };
  }, [toast.id, onAppearComplete]);

  useEffect(() => {
    if (animationState !== "visible") return;

    if (toast.isPaused) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
        // Calculate remaining time when paused
        const elapsed = Date.now() - pausedAtRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      }
      return;
    }

    // Resume or start timer
    pausedAtRef.current = Date.now();
    timerRef.current = window.setTimeout(() => {
      setAnimationState("disappearing");
      window.setTimeout(() => {
        onRemove(toast.id);
      }, TOAST_DISAPPEAR_DURATION);
    }, remainingTimeRef.current);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [animationState, toast.isPaused, toast.id, onRemove]);

  const handleMouseEnter = () => {
    if (animationState === "visible" && !toast.isPaused) {
      onPause(toast.id);
    }
  };

  const handleMouseLeave = () => {
    if (animationState === "visible" && toast.isPaused) {
      onResume(toast.id);
    }
  };

  const handleTouchStart = () => {
    if (animationState === "visible" && !toast.isPaused) {
      onPause(toast.id);
    }
  };

  const handleTouchEnd = () => {
    if (animationState === "visible" && toast.isPaused) {
      onResume(toast.id);
    }
  };

  const handleClose = () => {
    setAnimationState("disappearing");
    window.setTimeout(() => {
      onRemove(toast.id);
    }, TOAST_DISAPPEAR_DURATION);
  };

  const typeClass =
    styles[`toast${toast.type.charAt(0).toUpperCase()}${toast.type.slice(1)}`];
  const stateClass =
    styles[
      `toast${animationState.charAt(0).toUpperCase()}${animationState.slice(1)}`
    ];

  return (
    <div
      className={`${styles.toast} ${typeClass} ${stateClass}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.message}>{toast.message}</div>
      <button
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="Close notification"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

// Memoize to prevent re-renders when toast props haven't changed
export const MemoizedToast = memo(Toast, (prevProps, nextProps) => {
  // Only re-render if these properties change
  return (
    prevProps.toast.id === nextProps.toast.id &&
    prevProps.toast.message === nextProps.toast.message &&
    prevProps.toast.type === nextProps.toast.type &&
    prevProps.toast.isPaused === nextProps.toast.isPaused &&
    prevProps.toast.isAppearing === nextProps.toast.isAppearing &&
    prevProps.onRemove === nextProps.onRemove &&
    prevProps.onPause === nextProps.onPause &&
    prevProps.onResume === nextProps.onResume &&
    prevProps.onAppearComplete === nextProps.onAppearComplete
  );
});
